import { CacheStrategy } from '@rammetec/data-access';
import { AUDIT_FIELD_DESCRIPTORS, AUDIT_FOREIGN_KEYS } from './_auditFields.js';
import { LIFECYCLE_FIELD_DESCRIPTORS } from './_lifecycleFields.js';
import { BUSINESS_ALLOWED_DATA_SOURCES, BUSINESS_DEFAULT_DATA_SOURCE } from './dataSourcePolicy.js';
import { PRODUCT_GROUP, ProductType } from '../types/product-group.js';
import { FIELD_LIMITS } from '../validation/fieldLimits.js';
/**
 * Descriptor for ProductGroup entity.
 * Stored in 'org/{orgId}/product-group'.
 *
 * Fields without groups are always persisted.
 * Fields with groups are persisted only when those groups are active.
 *
 * `path` / `level` / `pathDescription` are service-filled (request 0347) — not
 * editable in forms; listed in SERVICE_FILLED_NAMES so import/assistant skip them.
 */
export const productGroupDescriptor = {
    name: PRODUCT_GROUP,
    label: 'Grupo de produto',
    domain: 'catalog',
    collectionPath: 'org/{orgId}/product-group',
    pathPatternKeys: {
        orgId: 'string',
    },
    version: 1,
    description: 'Represents a group/category of products in the catalog',
    repositoryConfig: {
        dataSource: BUSINESS_DEFAULT_DATA_SOURCE,
        cache: CacheStrategy.InMemory,
    },
    allowedDataSources: BUSINESS_ALLOWED_DATA_SOURCES,
    fields: [
        {
            name: 'id',
            label: 'ID',
            type: 'string',
            required: true,
            // No groups = always persisted
            description: 'Unique product group identifier',
        },
        {
            name: 'code',
            example: 100,
            label: 'Código',
            type: 'number',
            display: 'code',
            required: true,
            unique: true,
            autoNumber: true,
            description: 'Numeric product-group code (master-data identifier; non-negative, capped at MAX_CODE_SMALL)',
        },
        {
            name: 'description',
            example: 'Malhas',
            label: 'Descrição',
            type: 'string',
            required: true,
            maxLength: FIELD_LIMITS.NAME,
            description: 'Product group description',
        },
        {
            name: 'parentId',
            example: 'parent-group-id',
            label: 'Grupo pai',
            type: 'string',
            required: false,
            description: 'Parent product group id; null/absent for a root group',
        },
        {
            name: 'path',
            example: 'g1/g2',
            label: 'Caminho (ids)',
            type: 'string',
            required: false,
            maxLength: 200,
            description: 'Service-filled ancestor ids + self joined by /',
        },
        {
            name: 'level',
            example: 1,
            label: 'Nível',
            type: 'number',
            required: false,
            description: 'Service-filled depth (1 = root, integer; max PRODUCT_GROUP_MAX_DEPTH)',
        },
        {
            name: 'pathDescription',
            example: 'Tecidos › Malha',
            label: 'Caminho',
            type: 'string',
            required: false,
            // 4 × NAME + separators
            maxLength: 700,
            description: 'Service-filled description path joined with ›',
        },
        {
            name: 'productType',
            example: 'Textil',
            label: 'Tipo de produto',
            type: 'string',
            required: true,
            description: `Defines the product group's type (e.g., ${ProductType.BASIC} or ${ProductType.TEXTIL})`,
            defaultValue: ProductType.BASIC,
        },
        ...LIFECYCLE_FIELD_DESCRIPTORS,
        ...AUDIT_FIELD_DESCRIPTORS,
    ],
    foreignKeys: [
        ...AUDIT_FOREIGN_KEYS,
        {
            fieldName: 'parentId',
            targetEntity: PRODUCT_GROUP,
            relationship: 'many-to-one',
            description: 'Parent product group (null = root)',
            displayField: 'pathDescription',
        },
    ],
};
//# sourceMappingURL=product-group.descriptor.js.map